/**
 * Table Lobby Component - List and create multiplayer tables
 */

import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { Plus, Users, Crown, Eye, Lock, Loader2, Trash2, Shield } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useAuth } from '@/features/auth/contexts/AuthContext'
import {
  getTables,
  getAllTables,
  createTable,
  joinTable,
  joinAsSpectator,
  deleteTable,
  getUserRole,
  TableData,
} from '../services/multiplayerService'

interface TableLobbyProps {
  onPlaySolo: () => void
  onJoinTable: (tableId: string) => void
}

export function TableLobby({ onPlaySolo, onJoinTable }: TableLobbyProps) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const [tables, setTables] = useState<TableData[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [isJoining, setIsJoining] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState('public')
  const [userRole, setUserRole] = useState<'gangster' | 'patriarch' | 'don'>('gangster')

  // Delete confirmation
  const [tableToDelete, setTableToDelete] = useState<TableData | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  // Create table form
  const [tableName, setTableName] = useState('')
  const [minBet, setMinBet] = useState(100)
  const [isPrivate, setIsPrivate] = useState(false)
  const [tablePassword, setTablePassword] = useState('')
  const [isCreating, setIsCreating] = useState(false)

  // Load user role
  useEffect(() => {
    if (user?.uid) {
      getUserRole(user.uid).then(setUserRole)
    }
  }, [user?.uid])

  // Load tables
  useEffect(() => {
    loadTables()
    const interval = setInterval(loadTables, 10000)
    return () => clearInterval(interval)
  }, [userRole])

  const loadTables = async () => {
    // Patriarcas and DON can see all tables including private
    const data = userRole !== 'gangster' ? await getAllTables() : await getTables()
    setTables(data)
    setIsLoading(false)
  }

  const handleCreateTable = async () => {
    if (!user || !tableName.trim()) return

    setIsCreating(true)
    const tableId = await createTable(
      tableName.trim(),
      minBet,
      user.uid,
      user.displayName || 'Jugador',
      isPrivate,
      isPrivate ? tablePassword : undefined
    )
    setIsCreating(false)
    setIsCreateOpen(false)
    setTableName('')
    setIsPrivate(false)
    setTablePassword('')
    onJoinTable(tableId)
  }

  const handleJoinTable = async (tableId: string) => {
    if (!user) return

    setIsJoining(tableId)
    const result = await joinTable(tableId, user.uid, user.displayName || 'Jugador')

    if (result.success) {
      onJoinTable(tableId)
    } else {
      alert(result.error)
    }
    setIsJoining(null)
  }

  const handleSpectate = async (tableId: string) => {
    if (!user) return

    setIsJoining(tableId)
    const success = await joinAsSpectator(tableId, user.uid)

    if (success) {
      onJoinTable(tableId)
    } else {
      alert('Error al unirse como espectador')
    }
    setIsJoining(null)
  }

  const handleDeleteTable = async () => {
    if (!user || !tableToDelete) return

    setIsDeleting(true)
    const result = await deleteTable(tableToDelete.id, user.uid, userRole)

    if (result.success) {
      setTables(tables.filter(t => t.id !== tableToDelete.id))
    } else {
      alert(result.error)
    }
    setIsDeleting(false)
    setTableToDelete(null)
  }

  const canDeleteTable = (table: TableData) => {
    if (!user) return false
    // Capo can delete their own table, Patriarch/DON can delete any
    return table.capoId === user.uid || userRole === 'patriarch' || userRole === 'don'
  }

  const publicTables = tables.filter((t) => !t.isPrivate)
  const privateTables = tables.filter((t) => t.isPrivate)
  const availableTables = publicTables.filter((t) => t.players.length < t.maxPlayers)

  const getRoleBadge = () => {
    if (userRole === 'don') return { text: 'DON', color: 'bg-red-600' }
    if (userRole === 'patriarch') return { text: 'PATRIARCA', color: 'bg-purple-600' }
    return null
  }

  const roleBadge = getRoleBadge()

  return (
    <div className="p-4 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="font-cinzel text-2xl text-casino-gold">
            {t('games.blackjackMultiplayer')}
          </h2>
          {roleBadge && (
            <span className={`text-xs px-2 py-1 rounded ${roleBadge.color} text-white flex items-center gap-1`}>
              <Shield className="w-3 h-3" />
              {roleBadge.text}
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onPlaySolo}>
            {t('multiplayer.playSolo')}
          </Button>
          <Button variant="gold" onClick={() => setIsCreateOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Crear Mesa
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="public">{t('multiplayer.public')}</TabsTrigger>
          <TabsTrigger value="available">{t('multiplayer.available')}</TabsTrigger>
          {userRole !== 'gangster' && (
            <TabsTrigger value="private" className="flex items-center gap-1">
              <Lock className="w-3 h-3" />
              Privadas
            </TabsTrigger>
          )}
          {userRole !== 'gangster' && (
            <TabsTrigger value="all">Todas</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="public" className="mt-4">
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="w-6 h-6 animate-spin text-casino-gold" />
            </div>
          ) : publicTables.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-gray-400">
                No hay mesas disponibles. ¡Crea una!
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {publicTables.map((table) => (
                <TableCard
                  key={table.id}
                  table={table}
                  isJoining={isJoining === table.id}
                  canDelete={canDeleteTable(table)}
                  onJoin={() => handleJoinTable(table.id)}
                  onSpectate={() => handleSpectate(table.id)}
                  onDelete={() => setTableToDelete(table)}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="available" className="mt-4">
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="w-6 h-6 animate-spin text-casino-gold" />
            </div>
          ) : availableTables.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-gray-400">
                No hay mesas con espacio disponible. ¡Crea una!
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {availableTables.map((table) => (
                <TableCard
                  key={table.id}
                  table={table}
                  isJoining={isJoining === table.id}
                  canDelete={canDeleteTable(table)}
                  onJoin={() => handleJoinTable(table.id)}
                  onSpectate={() => handleSpectate(table.id)}
                  onDelete={() => setTableToDelete(table)}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {userRole !== 'gangster' && (
          <TabsContent value="private" className="mt-4">
            {privateTables.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center text-gray-400">
                  No hay mesas privadas
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                {privateTables.map((table) => (
                  <TableCard
                    key={table.id}
                    table={table}
                    isJoining={isJoining === table.id}
                    canDelete={canDeleteTable(table)}
                    onJoin={() => handleJoinTable(table.id)}
                    onSpectate={() => handleSpectate(table.id)}
                    onDelete={() => setTableToDelete(table)}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        )}

        {userRole !== 'gangster' && (
          <TabsContent value="all" className="mt-4">
            {tables.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center text-gray-400">
                  No hay mesas
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                {tables.map((table) => (
                  <TableCard
                    key={table.id}
                    table={table}
                    isJoining={isJoining === table.id}
                    canDelete={canDeleteTable(table)}
                    onJoin={() => handleJoinTable(table.id)}
                    onSpectate={() => handleSpectate(table.id)}
                    onDelete={() => setTableToDelete(table)}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        )}
      </Tabs>

      {/* Create Table Modal */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Crear Nueva Mesa</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Nombre de la Mesa</Label>
              <Input
                value={tableName}
                onChange={(e) => setTableName(e.target.value)}
                placeholder="Mi Mesa VIP"
                maxLength={30}
              />
            </div>
            <div className="space-y-2">
              <Label>Apuesta Mínima</Label>
              <div className="flex gap-2">
                {[100, 500, 1000, 5000].map((bet) => (
                  <Button
                    key={bet}
                    type="button"
                    variant={minBet === bet ? 'gold' : 'outline'}
                    size="sm"
                    onClick={() => setMinBet(bet)}
                    className="flex-1"
                  >
                    ${bet.toLocaleString()}
                  </Button>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="private-switch">Mesa Privada</Label>
              <Switch
                id="private-switch"
                checked={isPrivate}
                onCheckedChange={setIsPrivate}
              />
            </div>
            {isPrivate && (
              <div className="space-y-2">
                <Label>Contraseña</Label>
                <Input
                  type="password"
                  value={tablePassword}
                  onChange={(e) => setTablePassword(e.target.value)}
                  placeholder="Contraseña para unirse"
                />
              </div>
            )}
            <Button
              variant="gold"
              className="w-full"
              onClick={handleCreateTable}
              disabled={isCreating || !tableName.trim() || (isPrivate && !tablePassword)}
            >
              {isCreating ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Plus className="w-4 h-4 mr-2" />
              )}
              Crear Mesa {isPrivate ? 'Privada' : 'Pública'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!tableToDelete} onOpenChange={() => setTableToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Borrar mesa "{tableToDelete?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Todos los jugadores serán expulsados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteTable}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Borrar Mesa
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

// Table Card Component
function TableCard({
  table,
  isJoining,
  canDelete,
  onJoin,
  onSpectate,
  onDelete,
}: {
  table: TableData
  isJoining: boolean
  canDelete: boolean
  onJoin: () => void
  onSpectate: () => void
  onDelete: () => void
}) {
  const { t } = useTranslation()
  const isFull = table.players.length >= table.maxPlayers

  const getGameStateLabel = () => {
    switch (table.gameState) {
      case 'waiting':
        return { text: 'Esperando', color: 'bg-yellow-600' }
      case 'betting':
        return { text: 'Apostando', color: 'bg-blue-600' }
      case 'playing':
        return { text: 'Jugando', color: 'bg-green-600' }
      case 'dealerTurn':
        return { text: 'Dealer', color: 'bg-purple-600' }
      case 'ended':
        return { text: 'Ronda Terminada', color: 'bg-gray-600' }
      default:
        return { text: '', color: '' }
    }
  }

  const state = getGameStateLabel()

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <Card className="hover:border-casino-gold/50 transition-colors">
        <CardContent className="p-4">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h3 className="font-semibold flex items-center gap-2">
                {table.isPrivate && <Lock className="w-4 h-4 text-gray-400" />}
                {table.name}
              </h3>
              <div className="flex items-center gap-2 mt-1 text-sm text-gray-400">
                <span className="flex items-center gap-1">
                  <Users className="w-3 h-3" />
                  {table.players.length}/{table.maxPlayers}
                </span>
                <span>|</span>
                <span>{t('multiplayer.min')} ${table.minBet.toLocaleString()}</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-xs px-2 py-1 rounded ${state.color} text-white`}>
                {state.text}
              </span>
              {canDelete && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onDelete}
                  className="h-7 w-7 p-0 text-red-500 hover:text-red-400 hover:bg-red-500/10"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>

          {/* Players */}
          <div className="flex flex-wrap gap-1 mb-3">
            {table.players.map((player) => (
              <span
                key={player.odIdBrief}
                className="text-xs bg-zinc-700 px-2 py-0.5 rounded flex items-center gap-1"
              >
                {player.odIdBrief === table.capoId && <Crown className="w-3 h-3 text-casino-gold" />}
                {player.name}
              </span>
            ))}
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            {isFull ? (
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={onSpectate}
                disabled={isJoining}
              >
                {isJoining ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Eye className="w-4 h-4 mr-2" />
                    Ver
                  </>
                )}
              </Button>
            ) : (
              <>
                <Button
                  variant="gold"
                  size="sm"
                  className="flex-1"
                  onClick={onJoin}
                  disabled={isJoining}
                >
                  {isJoining ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Unirse'
                  )}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onSpectate}
                  disabled={isJoining}
                >
                  <Eye className="w-4 h-4" />
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  )
}
